export interface CamelotKey {
  number: number; // 1-12
  letter: 'A' | 'B';
  formatted: string; // e.g. "8A"
}

// Color scheme based on DJ Circle of Fifths (Camelot Wheel)
// Each number (1-12) maps to a color with corresponding high-contrast text color
export const camelotColors: Record<number, { bg: string; text: string }> = {
  1: { bg: '#84B026', text: '#000000' },   // Lime Green
  2: { bg: '#C9D119', text: '#000000' },   // Yellow-Green
  3: { bg: '#FBD815', text: '#000000' },   // Yellow
  4: { bg: '#F7A71B', text: '#000000' },   // Orange
  5: { bg: '#E95221', text: '#ffffff' },   // Red-Orange
  6: { bg: '#D0192A', text: '#ffffff' },   // Red
  7: { bg: '#B22254', text: '#ffffff' },   // Magenta
  8: { bg: '#77357A', text: '#ffffff' },   // Purple
  9: { bg: '#3B4D8D', text: '#ffffff' },   // Indigo
  10: { bg: '#0083B8', text: '#ffffff' },  // Blue
  11: { bg: '#009BA5', text: '#ffffff' },  // Teal
  12: { bg: '#28A26A', text: '#ffffff' },  // Green
};

export function parseKey(keyStr: string | undefined | null): CamelotKey | null {
  if (!keyStr) return null;
  const clean = keyStr.trim().replace(/\s+/g, ' ');
  if (!clean) return null;

  // 1. Try Camelot format directly (e.g. "8A", "08A", "11B")
  const camelotMatch = clean.match(/^0?([1-9]|1[0-2])([abAB])$/);
  if (camelotMatch) {
    const num = parseInt(camelotMatch[1], 10);
    const letter = camelotMatch[2].toUpperCase() as 'A' | 'B';
    return { number: num, letter, formatted: `${num}${letter}` };
  }

  // 2. Try traditional musical key mapping
  const keyMap: Record<string, { number: number; letter: 'A' | 'B' }> = {
    // Majors (B)
    'C': { number: 8, letter: 'B' }, 'CMAJ': { number: 8, letter: 'B' }, 'C MAJOR': { number: 8, letter: 'B' },
    'C#': { number: 3, letter: 'B' }, 'C#MAJ': { number: 3, letter: 'B' }, 'C# MAJOR': { number: 3, letter: 'B' },
    'DB': { number: 3, letter: 'B' }, 'DBMAJ': { number: 3, letter: 'B' }, 'DB MAJOR': { number: 3, letter: 'B' },
    'D': { number: 10, letter: 'B' }, 'DMAJ': { number: 10, letter: 'B' }, 'D MAJOR': { number: 10, letter: 'B' },
    'D#': { number: 5, letter: 'B' }, 'D#MAJ': { number: 5, letter: 'B' }, 'D# MAJOR': { number: 5, letter: 'B' },
    'EB': { number: 5, letter: 'B' }, 'EBMAJ': { number: 5, letter: 'B' }, 'EB MAJOR': { number: 5, letter: 'B' },
    'E': { number: 12, letter: 'B' }, 'EMAJ': { number: 12, letter: 'B' }, 'E MAJOR': { number: 12, letter: 'B' },
    'F': { number: 7, letter: 'B' }, 'FMAJ': { number: 7, letter: 'B' }, 'F MAJOR': { number: 7, letter: 'B' },
    'F#': { number: 2, letter: 'B' }, 'F#MAJ': { number: 2, letter: 'B' }, 'F# MAJOR': { number: 2, letter: 'B' },
    'GB': { number: 2, letter: 'B' }, 'GBMAJ': { number: 2, letter: 'B' }, 'GB MAJOR': { number: 2, letter: 'B' },
    'G': { number: 9, letter: 'B' }, 'GMAJ': { number: 9, letter: 'B' }, 'G MAJOR': { number: 9, letter: 'B' },
    'G#': { number: 4, letter: 'B' }, 'G#MAJ': { number: 4, letter: 'B' }, 'G# MAJOR': { number: 4, letter: 'B' },
    'AB': { number: 4, letter: 'B' }, 'ABMAJ': { number: 4, letter: 'B' }, 'AB MAJOR': { number: 4, letter: 'B' },
    'A': { number: 11, letter: 'B' }, 'AMAJ': { number: 11, letter: 'B' }, 'A MAJOR': { number: 11, letter: 'B' },
    'A#': { number: 6, letter: 'B' }, 'A#MAJ': { number: 6, letter: 'B' }, 'A# MAJOR': { number: 6, letter: 'B' },
    'BB': { number: 6, letter: 'B' }, 'BBMAJ': { number: 6, letter: 'B' }, 'BB MAJOR': { number: 6, letter: 'B' },
    'B': { number: 1, letter: 'B' }, 'BMAJ': { number: 1, letter: 'B' }, 'B MAJOR': { number: 1, letter: 'B' },

    // Minors (A)
    'AM': { number: 8, letter: 'A' }, 'AMIN': { number: 8, letter: 'A' }, 'A MINOR': { number: 8, letter: 'A' },
    'A#M': { number: 3, letter: 'A' }, 'A#MIN': { number: 3, letter: 'A' }, 'A# MINOR': { number: 3, letter: 'A' },
    'BBM': { number: 3, letter: 'A' }, 'BBMIN': { number: 3, letter: 'A' }, 'BB MINOR': { number: 3, letter: 'A' },
    'BM': { number: 10, letter: 'A' }, 'BMIN': { number: 10, letter: 'A' }, 'B MINOR': { number: 10, letter: 'A' },
    'CM': { number: 5, letter: 'A' }, 'CMIN': { number: 5, letter: 'A' }, 'C MINOR': { number: 5, letter: 'A' },
    'C#M': { number: 12, letter: 'A' }, 'C#MIN': { number: 12, letter: 'A' }, 'C# MINOR': { number: 12, letter: 'A' },
    'DBM': { number: 12, letter: 'A' }, 'DBMIN': { number: 12, letter: 'A' }, 'DB MINOR': { number: 12, letter: 'A' },
    'DM': { number: 7, letter: 'A' }, 'DMIN': { number: 7, letter: 'A' }, 'D MINOR': { number: 7, letter: 'A' },
    'D#M': { number: 2, letter: 'A' }, 'D#MIN': { number: 2, letter: 'A' }, 'D# MINOR': { number: 2, letter: 'A' },
    'EBM': { number: 2, letter: 'A' }, 'EBMIN': { number: 2, letter: 'A' }, 'EB MINOR': { number: 2, letter: 'A' },
    'EM': { number: 9, letter: 'A' }, 'EMIN': { number: 9, letter: 'A' }, 'E MINOR': { number: 9, letter: 'A' },
    'FM': { number: 4, letter: 'A' }, 'FMIN': { number: 4, letter: 'A' }, 'F MINOR': { number: 4, letter: 'A' },
    'F#M': { number: 11, letter: 'A' }, 'F#MIN': { number: 11, letter: 'A' }, 'F# MINOR': { number: 11, letter: 'A' },
    'GBM': { number: 11, letter: 'A' }, 'GBMIN': { number: 11, letter: 'A' }, 'GB MINOR': { number: 11, letter: 'A' },
    'GM': { number: 6, letter: 'A' }, 'GMIN': { number: 6, letter: 'A' }, 'G MINOR': { number: 6, letter: 'A' },
    'G#M': { number: 1, letter: 'A' }, 'G#MIN': { number: 1, letter: 'A' }, 'G# MINOR': { number: 1, letter: 'A' },
    'ABM': { number: 1, letter: 'A' }, 'ABMIN': { number: 1, letter: 'A' }, 'AB MINOR': { number: 1, letter: 'A' },
  };

  const norm = clean.toUpperCase();
  const found = keyMap[norm];
  if (found) {
    return { number: found.number, letter: found.letter, formatted: `${found.number}${found.letter}` };
  }

  // Fallback parsing (e.g. "C#m7" or other suffixes)
  const isMinor = norm.endsWith('M') || norm.includes('MIN') || norm.includes('MINOR') || norm.includes('M7');
  const pitchRootMatch = norm.match(/^([A-G]#?|[A-G]B?)/);
  if (pitchRootMatch) {
    const pitch = pitchRootMatch[1];
    const lookupKey = isMinor ? `${pitch}M` : pitch;
    const secondFound = keyMap[lookupKey];
    if (secondFound) {
      return { number: secondFound.number, letter: secondFound.letter, formatted: `${secondFound.number}${secondFound.letter}` };
    }
  }

  return null;
}

export function areKeysCompatible(
  keyAStr: string | undefined | null,
  keyBStr: string | undefined | null,
  exact: boolean
): boolean {
  if (!keyAStr || !keyBStr) return false;

  const keyA = parseKey(keyAStr);
  const keyB = parseKey(keyBStr);

  if (!keyA || !keyB) return false;

  if (exact) {
    return keyA.formatted === keyB.formatted;
  }

  // Extended Compatibility Rules:
  // 1. Same key (8A and 8A)
  if (keyA.formatted === keyB.formatted) return true;

  // 2. Relative Major/Minor (same number, different letter: 8A and 8B)
  if (keyA.number === keyB.number) return true;

  // 3. Neighboring pitches (same letter, number +/- 1 modulo 12)
  if (keyA.letter === keyB.letter) {
    const diff = Math.abs(keyA.number - keyB.number);
    if (diff === 1 || diff === 11) return true;
  }

  return false;
}
